import { Layout, Menu, MenuProps } from 'antd';
import { LOGO } from 'assets/svg';
import { useUser } from 'contexts/UserProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import { icons } from 'utils/constants/icons';
import useRoutesByRole from 'hooks/useRoutesByRole';

type MenuItem = Required<MenuProps>['items'][number];

const selectedKeys = [
  [path.HO_FOLLOW_PLAN_DETAIL, path.HO_FOLLOW_PLAN],
  [path.CN_FOLLOW_PLAN, path.CN_FOLLOW_PLAN_DETAIL],
  [path.HO_PLAN, path.HO_PLAN_DETAIL],
  [path.CN_PLAN, path.CN_PLAN_DETAIL],
  [path.SALE_PLAN, path.TEAM_DASHBOARD],
  [path.PRODUCT_SLOC, path.PRODUCT_DETAIL],
  [path.SET_APPROVAL, path.ADD_APPROVAL, path.APPROVAL_DETAIL],
  [path.PAYMENT_SUMMARY, path.PAYMENT_SUMMARY_DETAIL],
  [path.PAYMENT_SUMMARY_V2, path.PAYMENT_SUMMARY_DETAIL_V2],
];

const getSelectedKey = (pathname: string) => {
  for (const keys of selectedKeys) if (keys.includes(pathname)) return keys;

  return [pathname];
};

const { RightOutlined, LeftOutlined } = icons;
const { Sider } = Layout;

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);

  const { user } = useUser();
  const routes = useRoutesByRole();
  const location = useLocation();
  const navigate = useNavigate();

  const getItem = useCallback(
    (
      label: React.ReactNode,
      key: React.Key,
      icon?: React.ReactNode,
      children?: MenuItem[],
    ) => {
      return {
        key,
        icon,
        children,
        label,
        onClick: (item) => navigate(item.key),
      } as MenuItem;
    },
    [navigate],
  );

  const [hasNavigated, setHasNavigated] = useState(false);

  useEffect(() => {
    if (typeof user === 'undefined') return;

    if (!hasNavigated && location.pathname === '/') {
      setHasNavigated(true);

      if (routes.length && routes[0].children?.length) {
        const firstRoute = routes[0].children[0];
        navigate(firstRoute.path);
      }
    }
  }, [hasNavigated, location.pathname, navigate, routes, user]);

  const items: MenuItem[] = useMemo(
    () =>
      routes.map((item) => {
        const children = item.children
          ?.filter((item) => !item?.isHidden)
          .map((child) => {
            return getItem(child.title, child.path);
          });

        return getItem(
          item.title,
          item.key,
          location.state?.key == 'plant' && item.title === 'Kế hoạch'
            ? item.activeIcon
            : item.icon,
          children,
        );
      }),
    [getItem, location.state?.key, routes],
  );

  return (
    <Sider
      collapsible
      width={262}
      trigger={null}
      collapsed={collapsed}
      theme="light"
      className="drop-shadow-lg relative "
      onCollapse={(value) => setCollapsed(value)}
    >
      <div
        onClick={() => setCollapsed((prev) => !prev)}
        className="z-50 absolute bg-white cursor-pointer top-[75px] right-[-10px] w-[24px] h-[24px] leading-[24px] rounded-full shadow-sm text-center border"
      >
        {collapsed ? <RightOutlined /> : <LeftOutlined />}
      </div>
      <div className="flex items-center justify-start py-5 ml-5">
        <LOGO
          onClick={() => navigate(`${path.ROOT}`)}
          className="cursor-pointer"
        />
      </div>
      <Menu
        mode="inline"
        items={items}
        style={{
          minWidth: 0,
          flex: 'auto',
          background: 'transparent',
        }}
        defaultOpenKeys={location.state?.key ? [location.state.key] : []}
        selectedKeys={getSelectedKey(location.pathname)}
        onSelect={(e) => {
          navigate(e.keyPath[0], {
            state: { key: e.keyPath.length > 1 ? e.keyPath[1] : undefined },
          });
        }}
      />
    </Sider>
  );
};

export default Sidebar;
